import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import type {
	ReferenceDrivenRelationRuntimeRecord,
	SketchReferenceRelationSemantics,
} from '../model/SketchEntityReferenceRecord';
import type {
	AnyRuntimeRecord,
	BaseRelationBinding,
} from '../../../../types/reference/sketch-entity-reference/BaseReferenceDrivenRelationService';

export type {
	BaseRelationBinding,
} from '../../../../types/reference/sketch-entity-reference/BaseReferenceDrivenRelationService';

/**
 * 参考驱动关系服务基础类。
 */
export abstract class BaseReferenceDrivenRelationService<
	TRecord extends AnyRuntimeRecord,
	TBinding extends BaseRelationBinding<TRecord>,
> {
	private readonly bindingMap = new Map<string, TBinding>();

	/**
	 * 读取 binding。
	 *
	 * @param referenceId 引用记录 id。
	 * @returns binding 或 undefined。
	 */
	protected getBinding(referenceId: string): TBinding | undefined {
		return this.bindingMap.get(referenceId);
	}

	/**
	 * 写入 binding。
	 *
	 * @param referenceId 引用记录 id。
	 * @param binding binding。
	 */
	protected setBinding(referenceId: string, binding: TBinding): void {
		this.bindingMap.set(referenceId, binding);
	}

	/**
	 * 删除 binding。
	 *
	 * @param referenceId 引用记录 id。
	 */
	protected deleteBinding(referenceId: string): void {
		this.bindingMap.delete(referenceId);
	}

	/**
	 * 读取运行时记录。
	 *
	 * @param referenceId 引用记录 id。
	 * @returns 运行时记录或 null。
	 */
	public getRecord(referenceId: string): TRecord | null {
		return this.getBinding(referenceId)?.record ?? null;
	}

	/**
	 * 构造统一投影诊断。
	 *
	 * @param input 诊断输入。
	 * @returns 规范化诊断。
	 */
	protected createProjectionDiagnostic(input: {
		kind: ExecutionDiagnostic['kind'];
		message: string;
		referenceId: string;
		sourceRefEntityId: string;
		targetEntityId: string;
	}): ExecutionDiagnostic {
		return createExecutionDiagnostic({
			kind: input.kind,
			level: 'error',
			message: input.message,
			referenceId: input.referenceId,
			sourceRefEntityId: input.sourceRefEntityId,
			targetEntityId: input.targetEntityId,
		});
	}
}
